<template>
  <div class="h-screen overflow-hidden flex flex-col bg-white border-solid border-l border-0 border-gray-border px-8">
    <h2 class="mt-8 font-semibold">Create New Integration</h2>

    <form @submit.prevent="createIntegration" class="flex flex-col justify-between" style="height: calc(100% - 3rem);">
      <div class="flex flex-col gap-4 mt-4 overflow-visible">
        <div class="w-full">
          <base-label id="integrationType" label="Type"/>
          <dropdown-button name="selectIntegrationType"
                          primary-button-variant="secondary"
                          primary-button-classes="w-full h-10"
                          dropdown-position="below-left"
                          class="mt-1 w-full"
                          arrow-color="gray"
                          :value="integrationType"
                          :options="integrationTypeOptions"
                          :close-on-select="true"
                          @select-button="handleSelectIntegrationType"
          />
        </div>

        <email-helpdesk-integration-form v-if="integrationType === INTEGRATION_TYPES.EMAIL_HELPDESK"
                                         :form-data="emailHelpdeskFormData"
                                         :selected-bot-id="selectedBotId"
                                         :field-errors="fieldErrors"
                                         :timezone-options="timezoneOptions"
                                         :bot-options="botOptions"
                                         :loaders="loaders"
                                         :is-edit-mode="false"
                                         @update:email="email = $event"
                                         @update:subject="subject = $event"
                                         @update:format="format = $event"
                                         @update:sendgridTemplateId="sendgridTemplateId = $event"
                                         @update:timezone="timezone = $event"
                                         @update:botId="handleBotChange"
        />

      </div>

      <busy-button id="createButton"
                   variant="primary"
                   class="w-1/2 mb-10"
                   :is-loading="isLoading"
                   :is-disabled="isLoading"
                   @click="createIntegration"
      >
        <span class="w-full">Create Integration</span>
      </busy-button>

    </form>
  </div>
</template>

<script>
import BusyButton from '@/components/common/BusyButton'
import DropdownButton from '@/components/common/DropdownButton.vue'
import EmailHelpdeskIntegrationForm from '@/components/settings/integrations/EmailHelpdeskIntegrationForm.vue'
import { INTEGRATION_TYPES, SENDGRID_TEMPLATES, EMAIL_FORMATS } from '@/utils/integration.constants'
import integrationService from '@/services/amio/integration.service'
import toaster from '@/utils/toaster'
import integrationFormMixin from '@/utils/integration-form.mixin'

export default {
  name: 'SettingsIntegrationCreate',
  components: { BusyButton, DropdownButton, EmailHelpdeskIntegrationForm },
  mixins: [integrationFormMixin],

  data() {
    return {
      INTEGRATION_TYPES,
      integrationType: INTEGRATION_TYPES.EMAIL_HELPDESK,
      email: '',
      subject: '',
      format: EMAIL_FORMATS.HTML,
      sendgridTemplateId: SENDGRID_TEMPLATES.HTML,
      timezone: 'Europe/Prague',
      selectedBotId: '',
      isLoading: false,
      error: null
    }
  },

  computed: {
    integrationTypeOptions() {
      return [
        { title: 'Email', value: INTEGRATION_TYPES.EMAIL_HELPDESK }
      ]
    },

    emailHelpdeskFormData() {
      return {
        email: this.email,
        subject: this.subject,
        format: this.format,
        sendgrid_template_id: this.sendgridTemplateId,
        timezone: this.timezone
      }
    }
  },

  watch: {
    format(newFormat) {
      if (newFormat === EMAIL_FORMATS.TEXT) {
        this.sendgridTemplateId = SENDGRID_TEMPLATES.TEXT
      } else if (newFormat === EMAIL_FORMATS.HTML) {
        this.sendgridTemplateId = SENDGRID_TEMPLATES.HTML
      }
    }
  },

  methods: {
    handleSelectIntegrationType(index) {
      this.integrationType = this.integrationTypeOptions[index].value
    },

    async createIntegration() {
      this.isLoading = true
      this.error = null
      this.fieldErrors = {}

      try {
        // Validate required bot
        if (!this.selectedBotId) {
          this.fieldErrors = { bots: 'Bot is required' }
          this.isLoading = false
          return
        }

        const integrationData = {
          type: this.integrationType
        }

        if (this.integrationType === INTEGRATION_TYPES.EMAIL_HELPDESK) {
          integrationData.email = this.email
          integrationData.subject = this.subject
          integrationData.format = this.format
          integrationData.sendgrid_template_id = this.sendgridTemplateId
          integrationData.timezone = this.timezone
        }

        const newIntegration = await integrationService.createIntegration(this.selectedBotId, integrationData)
        this.$emit('process-new-integration', newIntegration)
        toaster.success('Integration created successfully')
      } catch (err) {
        this.handleApiError(err)
      } finally {
        this.isLoading = false
      }
    }
  },

  async mounted() {
    await this.loadBots()
  }
}
</script>
